import React, { useState, useCallback, useEffect } from "react";
import AuthService from "../../services/auth.service";
import { Breadcrumb, Button, Form, Input, Radio, Select, Tabs } from "antd";
import { allowOnlyNumbers, amountFormat } from "../../services/common.service";
import { useNavigate } from "react-router";
import CustomSlider from "../../components/slider";
import CustomNumberInput from "../../components/customNumberInput";
import { CONFIG } from "../../environment";
import FloatNumberInput from "../../components/floatNumberInput";
import { useTranslation } from "react-i18next";
import ReactApexChart from "react-apexcharts";
import PerfectScrollbar from "react-perfect-scrollbar";
import "../../layout/AuthLayout/authLayout.css";
import CustomNumberInputEMI from "../../components/customNumberInputEMI";

const options = {
  labels: ["Interest Amount", "Maturity Amount"],
  colors: ["#008FFB", "#00E396"],
  chart: {
    type: "donut",
  },
  plotOptions: {
    pie: {
      startAngle: -90,
      endAngle: 90,
      offsetY: 10,
    },
  },
  grid: {
    padding: {
      bottom: -80,
    },
  },
  responsive: [
    {
      breakpoint: 480,
      options: {
        chart: {
          width: 200,
        },
        legend: {
          position: "bottom",
        },
      },
    },
  ],
};

export default function LoanDepositCalculator() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [stage, setStage] = useState(0);
  const [isDeposit, setIsDeposit] = useState(false);
  const [dataSource, setDataSource] = useState();
  const [activeKey, setActiveKey] = useState("LA");
  const [details, setDetails] = useState({
    loanAmount: 200000,
    loanTenure: 24,
    depositTenure: 24,
    depositAmount: 10000,
    loanInterestRate: 8.5,
    depositInterestRate: 8.5,
    periodUnit: "",
    compoundFrequency: "",
  });
  const [periodUnits, setPeriodUnits] = useState([]);
  const [compoundFrequencies, setCompoundFrequencies] = useState([]);
  const [isCompound, setIsCompound] = useState(false);
  const [accounType, setAccounType] = useState("D");

  useEffect(() => {
    if (activeKey === "TD") {
      if (CONFIG.IS_ADDITIONAL_DEPOSIT_CALCULATOR) {
        setAccounType("D");
        getInterestValues();
      }
      setStage(2);
    } else if (activeKey === "LA") {
      setStage(1);
    }
    setDataSource(null);
    form.setFieldsValue({ ...details });
  }, [activeKey]);

  const getInterestValues = useCallback(async () => {
    try {
      const response = await AuthService.getinterestValues();
      if (response.status === 200) {
        setPeriodUnits(response.data["data"]["periodUnits"]);
        setCompoundFrequencies(response.data["data"]["compoundFrequency"]);
      }
    } catch (error) {}
  }, [details]);

  const calculateDepositResult = useCallback(async () => {
    try {
      setIsDeposit(true);
      const response = await AuthService.calculateLoanDepositResult({
        accountType: accounType,
        principalAmount: details.depositAmount,
        tenor: details.depositTenure,
        interestRate: details.depositInterestRate,
        periodUnit: details.periodUnit,
        compoundFrequency: details.compoundFrequency,
      });
      if (response.status === 200) {
        setDataSource(response.data);
      }
    } catch (error) {}
  }, [details, accounType]);

  const calculateLoanResult = useCallback(async () => {
    try {
      setIsDeposit(false);
      const response = await AuthService.calculateLoanDepositResult({
        accountType: "L",
        principalAmount: details?.loanAmount,
        tenor: details?.loanTenure,
        interestRate: details?.loanInterestRate,
      });
      if (response.status === 200) {
        setDataSource(response.data);
      }
    } catch (err) {}
  }, [details]);

  const handleSliderChange = useCallback(
    (value, name) => {
      try {
        setDetails({ ...details, [name]: value });
        form.setFieldsValue({
          [name]: value,
        });
      } catch (error) {}
    },
    [details]
  );

  const handleAccountType = useCallback((e) => {
    setAccounType(e.target.value);
    setIsCompound(e.target.value === "C" ? true : false);
  });

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderLoanForm();

      case 2:
        return renderDepositForm();

      default:
        return renderLoanForm();
    }
  }

  function renderLoanForm() {
    return (
      <>
        <Form onFinish={calculateLoanResult} layout="vertical" form={form}>
          <div className="col-12 ">
            <div className="row">
              <div
                className="col-12 col-sm-6 col-md-12 col-lg-6"
                style={{ padding: "10px" }}
              >
                {t("pages.my_accounts.loans.loan_amount", {
                  defaultValue: "Investment Amount",
                })}
              </div>
              {/* <div className="col-12 col-sm-6 col-md-5 col-lg-6">
                <CustomNumberInput
                  name="loanAmount"
                  minLimit={1000}
                  maxLimit={100000000}
                  prefix={CONFIG.CURRENCY}
                  onChange={(value) => handleSliderChange(value, "loanAmount")}
                />
              </div> */}
            </div>
            {/* <CustomSlider
              min={1000}
              max={100000000}
              disabled={false}
              value={details.loanAmount}
              tipFormatter={(value) => amountFormat(value)}
              onChange={(value) => handleSliderChange(value, "loanAmount")}
              className="mt-0"
              formatValues={true}
            /> */}
            <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12">
             <CustomNumberInputEMI
                name="loanAmount"
                minLimit={1000}
                prefix={CONFIG.CURRENCY}
                onChange={(value) => handleSliderChange(value, "loanAmount")}
                />
                </div>
          </div>
          <div className="col-12">
            <div className="row">
              <div
                className="col-12 col-sm-6 col-md-12 col-lg-7"
                style={{ padding: "10px" }}
              >
                {t("pages.my_accounts.deposits.tenure", {
                  defaultValue: "Tenure",
                })}
              </div>
              <div className="col-12 col-sm-6 col-md-5 col-lg-5">
                <CustomNumberInput
                  name="loanTenure"
                  maxLimit={60}
                  minLimit={12}
                  prefix="Months"
                  onChange={(value) => handleSliderChange(value, "loanTenure")}
                />
              </div>
            </div>
            <CustomSlider
              min={12}
              max={60}
              disabled={false}
              value={details.loanTenure}
              tipFormatter={(value) => {
                return `${value} months`;
              }}
              onChange={(value) => handleSliderChange(value, "loanTenure")}
              className="mt-0"
              type="months"
            />
          </div>

          <div className="col-12">
            <div className="row">
              <div
                className="col-12 col-sm-6 col-md-12 col-lg-7"
                style={{ padding: "10px" }}
              >
                {t("pages.landing.interest_rate", {
                  defaultValue: "Profit Rate",
                })}
              </div>
              <div className="col-12 col-sm-6 col-md-5 col-lg-5">
                <FloatNumberInput
                  name="loanInterestRate"
                  minLimit={8}
                  maxLimit={30}
                  prefix="%"
                  onChange={(value) =>
                    handleSliderChange(value, "loanInterestRate")
                  }
                />
              </div>
            </div>
            <CustomSlider
              min={8}
              max={30}
              disabled={false}
              value={details.loanInterestRate}
              tipFormatter={(value) => {
                return `${value} %`;
              }}
              onChange={(value) =>
                handleSliderChange(value, "loanInterestRate")
              }
              className="mt-0"
              type="%"
              step={0.1}
            />
          </div>

          <div className="row justify-content-center">
            <div className="col-8">
              <Button
                htmlType="submit"
                type="primary"
                className="login-button mb-3"
                size="large"
                block
              >
                {t("buttons.calculate", {
                  defaultValue: "Calculate",
                })}
              </Button>
            </div>
          </div>
        </Form>
      </>
    );
  }

  function renderDepositForm() {
    return (
      <>
        <Form onFinish={calculateDepositResult} layout="vertical" form={form}>
          {CONFIG.IS_ADDITIONAL_DEPOSIT_CALCULATOR && (
            <Form.Item name="accountType">
              <Radio.Group
                className="d-flex"
                onChange={handleAccountType}
                defaultValue={accounType}
              >
                <Radio value={"D"}>
                  {" "}
                  {t("pages.landing.simple_interest", {
                    defaultValue: "Simple Interest",
                  })}
                </Radio>
                <Radio value={"C"}>
                  {" "}
                  {t("pages.landing.compound_interest", {
                    defaultValue: "Compound Interest",
                  })}
                </Radio>
              </Radio.Group>
            </Form.Item>
          )}
          <div className="col-12 ">
            <div className="row">
              <div
                className="col-12 col-sm-6 col-md-12 col-lg-6"
                style={{ padding: "10px" }}
              >
                {t("pages.landing.deposit_amount", {
                  defaultValue: "Deposit Amount",
                })}
              </div>
              <div className="col-12 col-sm-6 col-md-5 col-lg-6">
                <CustomNumberInput
                  name="depositAmount"
                  maxLimit={100000000}
                  minLimit={1000}
                  prefix={CONFIG.CURRENCY}
                  onChange={(value) =>
                    handleSliderChange(value, "depositAmount")
                  }
                />
              </div>
            </div>
            <CustomSlider
              min={1000}
              max={100000000}
              disabled={false}
              value={details.depositAmount}
              formatValues={true}
              tipFormatter={(value) => amountFormat(value)}
              onChange={(value) => handleSliderChange(value, "depositAmount")}
              className="mt-0"
            />
          </div>
          <div className="col-12">
            <div className="row">
              <div
                className="col-12 col-sm-6 col-md-12 col-lg-7"
                style={{ padding: "10px" }}
              >
                {t("pages.my_accounts.deposits.tenure", {
                  defaultValue: "Tenure",
                })}
              </div>
              <div className="col-12 col-sm-6 col-md-5 col-lg-5">
                <CustomNumberInput
                  name="depositTenure"
                  maxLimit={60}
                  minLimit={12}
                  prefix="Months"
                  onChange={(value) =>
                    handleSliderChange(value, "depositTenure")
                  }
                />
              </div>
            </div>
            <CustomSlider
              min={12}
              max={60}
              disabled={false}
              value={details.depositTenure}
              tipFormatter={(value) => {
                return `${value} months`;
              }}
              onChange={(value) => handleSliderChange(value, "depositTenure")}
              className="mt-0"
              type="months"
            />
          </div>

          <div className="col-12">
            <div className="row">
              <div
                className="col-12 col-sm-6 col-md-12 col-lg-7"
                style={{ padding: "10px" }}
              >
                {t("pages.landing.interest_rate", {
                  defaultValue: "Profit Rate",
                })}
              </div>
              <div className="col-12 col-sm-6 col-md-5 col-lg-5">
                <FloatNumberInput
                  name="depositInterestRate"
                  minLimit={8}
                  maxLimit={30}
                  prefix="%"
                  onChange={(value) =>
                    handleSliderChange(value, "depositInterestRate")
                  }
                />
              </div>
            </div>
            <CustomSlider
              min={8}
              max={30}
              disabled={false}
              value={details.depositInterestRate}
              tipFormatter={(value) => {
                return `${value} %`;
              }}
              onChange={(value) =>
                handleSliderChange(value, "depositInterestRate")
              }
              className="mt-0"
              type="%"
              step={0.1}
            />
          </div>

          {CONFIG.IS_ADDITIONAL_DEPOSIT_CALCULATOR && (
            <>
              <div className="col-12">
                <Form.Item name="periodUnit">
                  <Select
                    placeholder={t("pages.landing.period_units", {
                      defaultValue: "Period Units",
                    })}
                    size="large"
                    name="periodUnit"
                    value={details.periodUnit}
                  >
                    {Object.keys(periodUnits).map((option, idx) => (
                      <Select.Option key={idx} value={periodUnits[option]}>
                        {periodUnits[option]}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </div>
              {isCompound && (
                <div className="col-12">
                  <Form.Item name="compoundFrequency">
                    <Select
                      placeholder={t("pages.landing.compound_frequency", {
                        defaultValue: "Compound Frequency",
                      })}
                      size="large"
                      name="compoundFrequency"
                      value={details.compoundFrequency}
                    >
                      {Object.keys(compoundFrequencies).map((option, idx) => (
                        <Select.Option key={idx} value={periodUnits[option]}>
                          {compoundFrequencies[option]}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
              )}
            </>
          )}

          <div className="row justify-content-center">
            <div className="col-8">
              <Button
                htmlType="submit"
                type="primary"
                className="login-button mb-3"
                size="large"
                block
              >
                {t("buttons.calculate", {
                  defaultValue: "Calculate",
                })}
              </Button>
            </div>
          </div>
        </Form>
      </>
    );
  }

  return (
    <>
      <div className="" style={{ height: "inherit" }}>
        <div className="auth-card">
          <PerfectScrollbar>
            <div className="centered-button">
              <span
                style={{ fontSize: "20px", fontWeight: "700" , marginBottom:"1rem"}}
                className="primary-color"
              >
                {activeKey === "LA"
                  ? t("pages.landing.loan_emi", {
                      defaultValue: "Investment EMI ",
                    })
                  : t("pages.landing.deposit", {
                      defaultValue: "Deposit ",
                    })}
                {t("pages.landing.calculator", {
                  defaultValue: "Calculator",
                })}
              </span>
            </div>
            <div className="d-flex justify-content-center">
              <div className="card-slider margin-style col-10">
                <div className="row tab-navbar">
                {CONFIG.CALCULATE_DEPOSIT ? (
                  <> 
                  <div
                    className={activeKey === "LA" ? "col-6 select" : "col-6"}
                  >
                    <span
                      onClick={() => {
                        setActiveKey("LA");
                      }}
                    >
                      {t("pages.my_accounts.common_my_accounts.loans", {
                        defaultValue: "Investment",
                      })}
                    </span>
                  </div>
                  <div
                    className={activeKey === "TD" ? "col-6 select" : "col-6"}
                  >
                    <span
                      onClick={() => {
                        setActiveKey("TD");
                      }}
                    >
                      {t("pages.my_accounts.common_my_accounts.deposits", {
                        defaultValue: "Deposits",
                      })}
                    </span>
                  </div>
                  </>
                  ) : (
                    <div
                    className={activeKey === "LA" ? "col-12 select" : "col-12"}
                  >
                    <span
                      onClick={() => {
                        setActiveKey("LA");
                      }}
                    >
                      {t("pages.my_accounts.common_my_accounts.loans", {
                        defaultValue: "Investment",
                      })}
                    </span>
                  </div>
                  )}
                </div>
                {/* <Tabs
            defaultActiveKey="LA"
            type="line"
            onChange={(key) => setActiveKey(key)}
            activeKey={activeKey}
            centered
          >
            <Tabs.TabPane tab="Loans" key="LA">
              {" "}
            </Tabs.TabPane>
            <Tabs.TabPane tab= {t("pages.my_accounts.common_my_accounts.deposits", {
                    defaultValue: "Deposits",
                  })} key="TD">
              {" "}
            </Tabs.TabPane>
          </Tabs> */}
                <div>{renderSwitch()}</div>
                {dataSource && (
                  <div className="my-2 py-3 px-2 rounded-1 bold-text mb-5 loan-selected">
                    <div className="row my-3 ">
                      {isDeposit ? (
                        <div className="col-6 ">
                          {t("pages.my_accounts.deposits.interest_amount", {
                            defaultValue: "Interest Amount",
                          })}
                        </div>
                      ) : (
                        <div className="col-6 ">
                          {t("pages.landing.monthly_installment", {
                            defaultValue: "Monthly Installment",
                          })}
                        </div>
                      )}
                      <div className="col-6 font-weight-bold text-right">
                        <span>
                          {amountFormat(dataSource?.emiAmount?.toFixed(2)) || "-"}
                        </span>
                      </div>
                    </div>
                    <div className="row my-3 ">
                      {isDeposit ? (
                        <div className="col-6">
                          {t("pages.my_accounts.deposits.maturity_amount", {
                            defaultValue: "Maturity Amount",
                          })}
                        </div>
                      ) : (
                        <div className="col-6">
                          {t("pages.landing.total_amount", {
                            defaultValue: "Total Amount",
                          })}
                        </div>
                      )}
                      <div className="col-6 font-weight-bold text-right">
                        <span>
                          {amountFormat(dataSource?.totalAmount?.toFixed(2)) || "-"}
                        </span>
                      </div>
                    </div>

                    {CONFIG.IS_ADDITIONAL_DEPOSIT_CALCULATOR && (
                      <div>
                        <div id="chart">
                          <ReactApexChart
                            series={[
                              dataSource.emiAmount,
                              dataSource.totalAmount,
                            ]}
                            options={options}
                            type="donut"
                          />
                        </div>
                        <div id="html-dist"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </PerfectScrollbar>
        </div>
      </div>
    </>
  );
}
